<template>
    <ion-page>
        <standard-header title="Stock Takes" align="middle"/>
        <ion-content :fullscreen="true">
            <pull-down-refresh :callback="refresh" />
            <skeleton :rows="4" v-if="stocktake === null"/>
            <sub-title v-if="stocktake !== null">
                <template #actions>
                    <ion-button fill="outline" color="success" @click="signoff()" v-if="stocktake.has_concluded">
                        <ion-icon slot="icon-only" :icon="checkmarkDoneCircleOutline" />
                    </ion-button>
                    <ion-button size="large" fill="outline" shape="round" color="danger" v-if="linesToCount > 0 && !stocktake.has_concluded">
                        <ion-badge size="large" color="danger">{{ linesToCount }}</ion-badge>
                    </ion-button>
                </template>
                <template #default>
                    {{ stocktake.reference }}
                </template>
            </sub-title>
           <ion-searchbar v-if="stocktake && stocktake.id"
                          placeholder="Search location code or item"
                          @keypress.enter="doSearch($event.target.value)"
                          animated
                          mode="ios" />
            <div v-show="stocktake && stocktake.lines.length < 1">
                <no-results title="No Results">
                    This stock take has no more lines
                </no-results>
            </div>
            <ion-list v-if="stocktake">
                <ion-item button v-for="line in lines" :key="line.id" :detail="true" @click="goStockTake(line)">
                    <ion-icon :icon="checkmarkCircleOutline" color="success" v-if="line.is_checked" />
                    <ion-icon :icon="closeCircleOutline" color="danger" v-if="line.item_location.qty_reserved > 0" />
                    <ion-label class="ion-margin-start">
                        <h2>
                            {{ line.item.item_number }}
                            <small v-show="!line.location.is_unknown">{{ line.location.code }}</small>
                        </h2>
                        <p>
                            {{ line.item.name }}
                        </p>
                    </ion-label>
                </ion-item>
            </ion-list>
        </ion-content>
        <ion-footer>
            <ion-toolbar>
                <ion-buttons slot="start">
                    <ion-button fill="outline" @click="adjustPage(-10)" >
                        <ion-icon slot="icon-only" :icon="caretBackOutline"></ion-icon>
                    </ion-button>
                    <ion-button fill="outline" @click="adjustPage(-1)" >
                        <ion-icon slot="icon-only" :icon="removeOutline"></ion-icon>
                    </ion-button>
                </ion-buttons>
                <ion-item>
                    <ion-label>
                        Page
                    </ion-label>
                    <ion-input v-model.number="page" debounce="300" enterkeyhint="go" inputmode="numeric" min="0" placeholder="Page #" />
                    <ion-label>
                        of {{ totalPages }}
                    </ion-label>
                </ion-item>
                <ion-buttons slot="end" >
                    <ion-button fill="outline"  @click="adjustPage(1)">
                        <ion-icon slot="icon-only" :icon="addOutline"></ion-icon>
                    </ion-button>
                    <ion-button fill="outline" @click="adjustPage(10)" >
                        <ion-icon slot="icon-only" :icon="caretForwardOutline"></ion-icon>
                    </ion-button>
                </ion-buttons>
            </ion-toolbar>
        </ion-footer>
    </ion-page>
</template>

<script>
import {IonSearchbar, IonToolbar, IonInput, IonButtons, IonContent, IonItem, IonLabel, IonList, IonPage, modalController, IonIcon, IonButton, IonBadge, IonFooter} from '@ionic/vue';
import StandardHeader from "@/components/StandardHeader.vue";
import {alertController} from '@ionic/core';
import {closeCircleOutline, imageOutline, checkmarkCircleOutline, checkmarkDoneCircleOutline, removeOutline, addOutline, caretBackOutline, caretForwardOutline} from 'ionicons/icons';

import SubTitle from "@/components/SubTitle";
import NoResults from "@/components/NoResults";
import Skeleton from "@/components/Skeleton";
import PullDownRefresh from "@/components/PullDownRefresh";
import TakeLine from "@/views/modules/stocktake/pages/stocktake-lines/TakeLine";
import EventBus from "@/EventBus";
import LocationSorter from "@/services/LocationSorter";

export default {
    inject: ['$http', '$toast', '$validationAlert'],
    name: 'StockTakeLinesList',
    components: {
        PullDownRefresh,
        Skeleton,
        NoResults,
        SubTitle,
        StandardHeader,
        IonContent, IonPage, IonItem, IonList, IonLabel, IonIcon, IonButton, IonBadge,
        IonSearchbar, IonToolbar, IonButtons, IonFooter, IonInput
    },
    computed: {
        params() {
            return this.$route.params;
        },
        hideCount() {
            return this.stocktake.has_concluded;
        },
        linesToCount() {
            return this.stocktake.lines.length;
        },
        lines() {
            return LocationSorter.sortByLocationCodes(this.stocktake.lines);
        }
    },
    data() {
        return {
            search: '',
            page: 1,
            totalPages: 0,
            stocktake: null
        }
    },
    methods: {
        ionViewDidEnter() {
            this.refresh();
        },
        adjustPage(delta) {
            this.page = parseInt(this.page) + parseInt(delta);
            if (this.page < 1) {
                this.page = 1;
            }
            if (this.page > this.totalPages) {
                this.page = this.totalPages;
            }
            this.refresh();
        },
        signoff() {
            EventBus().emitter.emit('loading', true);
            this.$http
                .post(`/api/stocktakes/${this.stocktake.id}/signoff`)
                .then(() => {
                    this.$toast.success("Stocktake completed and signed off");
                    this.$router.back();
                }).catch(error => {
                    this.$validationAlert(error);
                }).finally(() => {
                    EventBus().emitter.emit('loading', false);
                });
        },
        goStockTake(line) {

            if (line.item_location && line.item_location.qty_reserved > 0) {
                alertController.create({
                    header: 'Stock Take Error',
                    message: 'You cannot perform a stock take on an item which has a reserved quantity',
                    buttons: ['Ok']
                }).then(a => {a.present()});
                return;
            }

            EventBus().emitter.emit('loading', true);
            this.$http.get(`/api/stocktakes/lines/${line.id}`, {
                params: {include: 'users,item,location,location.zone,stocktake,item.mpns,item.skus,item.image_urls'}
            }).then(response => {
                modalController.create({
                    componentProps: {
                        line: response.data
                    },
                    component: TakeLine,
                }).then(modal => {
                    modal.present();
                    modal.onDidDismiss().then(response => {
                        if (response.data.reopen && response.data.line) {
                            this.goStockTake(response.data.line);
                            return;
                        }
                        if (response.data) {
                            this.refresh();
                        }
                    });
                });
            }).finally(() => {
                EventBus().emitter.emit('loading', false);
            });
        },
        refresh(event = false) {
            EventBus().emitter.emit('loading', true);
            this.$http.get(`/api/stocktakes/${this.params.id}`, {
                params: {
                    page: parseInt(this.page),
                    fts: this.search,
                    include: 'users,lines,lines.item_location,lines.location,lines.location.zone,lines.item'
                }
            }).then(response => {
                this.stocktake = response.data;
                this.page = response.headers['x-pagination-page'];
                this.totalPages = response.headers['x-pagination-pages'];
            }).finally(() =>{
                EventBus().emitter.emit('loading', false);
                if (event && event.target.complete) {
                    event.target.complete();
                }
            });
        },
        doSearch(search) {
          this.search = search;
          this.refresh();
        }
    },
    setup() {
        return {closeCircleOutline, imageOutline, checkmarkCircleOutline, checkmarkDoneCircleOutline, removeOutline, addOutline, caretBackOutline, caretForwardOutline};
    }
}
</script>

